import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    ArrowLeft,
    Clock,
    Download,
    Loader2,
    Paperclip,
    Linkedin,
    Twitter,
    Link2,
    Check,
    ArrowUpRight,
} from 'lucide-react';
import { Seo, SITE_URL } from '@/lib/seo';
import { API, abs, formatDate } from '@/lib/mediaUrl';
import { humanSize } from '@/lib/format';
import { StoryCover, BrandedCover } from '@/components/StoryCover';

const ShareRow = ({ title, url }) => {
    const [copied, setCopied] = useState(false);
    const enc = encodeURIComponent(url);
    const encT = encodeURIComponent(title);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch {
            /* noop */
        }
    };

    const btn =
        'inline-flex items-center justify-center w-9 h-9 rounded-full border border-[var(--line-2)] bg-[var(--surface)] text-[var(--ink-2)] hover:text-[var(--accent)] hover:border-[var(--accent)] transition-colors';

    return (
        <div className="flex items-center gap-2" data-testid="story-share">
            <span className="hidden sm:inline font-mono-brand text-[9.5px] uppercase tracking-[0.22em] text-[var(--ink-3)] mr-1">
                Share
            </span>
            <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc}`}
                target="_blank"
                rel="noreferrer"
                data-cursor="hover"
                data-testid="story-share-linkedin"
                title="Share on LinkedIn"
                className={btn}
            >
                <Linkedin size={15} />
            </a>
            <a
                href={`https://twitter.com/intent/tweet?url=${enc}&text=${encT}`}
                target="_blank"
                rel="noreferrer"
                data-cursor="hover"
                data-testid="story-share-twitter"
                title="Share on X"
                className={btn}
            >
                <Twitter size={15} />
            </a>
            <button
                type="button"
                onClick={copy}
                data-cursor="hover"
                data-testid="story-share-copy"
                title="Copy link"
                className={btn}
            >
                {copied ? (
                    <Check size={15} className="text-[var(--accent)]" />
                ) : (
                    <Link2 size={15} />
                )}
            </button>
        </div>
    );
};

const RelatedStories = ({ currentSlug, currentTags = [] }) => {
    const [related, setRelated] = useState([]);

    useEffect(() => {
        let alive = true;
        fetch(`${API}/posts`)
            .then((r) => (r.ok ? r.json() : []))
            .then((all) => {
                if (!alive || !Array.isArray(all)) return;
                const tagset = new Set((currentTags || []).map((t) => t.toLowerCase()));
                const ranked = all
                    .filter((p) => p.slug !== currentSlug)
                    .map((p) => ({
                        p,
                        score: (p.tags || []).reduce(
                            (n, t) => n + (tagset.has(String(t).toLowerCase()) ? 1 : 0),
                            0
                        ),
                    }))
                    .sort((a, b) => {
                        if (b.score !== a.score) return b.score - a.score;
                        return (
                            new Date(b.p.published_at || 0) -
                            new Date(a.p.published_at || 0)
                        );
                    })
                    .slice(0, 3)
                    .map((x) => x.p);
                setRelated(ranked);
            })
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, [currentSlug, currentTags]);

    if (related.length === 0) return null;

    return (
        <section
            data-testid="related-stories"
            className="mt-16 md:mt-20 pt-10 border-t border-[var(--line)]"
        >
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="inline-flex items-center gap-3 font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                    <span className="w-6 h-[1px] bg-[var(--line-2)]" />
                    <span>Keep reading</span>
                </div>
                <Link
                    to="/stories"
                    data-cursor="hover"
                    className="inline-flex items-center gap-1.5 font-mono-brand text-[10.5px] uppercase tracking-[0.2em] text-[var(--accent)] hover:opacity-80 transition-opacity"
                >
                    All stories <ArrowUpRight size={13} />
                </Link>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {related.map((p, i) => (
                    <motion.div
                        key={p.id}
                        initial={{ opacity: 0, y: 18 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-8%' }}
                        transition={{ duration: 0.5, delay: i * 0.06 }}
                    >
                        <Link
                            to={`/stories/${p.slug}`}
                            data-testid={`related-card-${p.slug}`}
                            data-cursor="hover"
                            className="group flex flex-col h-full rounded-2xl border border-[var(--line)] bg-[var(--surface)] overflow-hidden hover:border-[var(--line-2)] transition-colors"
                        >
                            <div className="relative aspect-[16/10] overflow-hidden bg-[var(--surface-2)]">
                                <StoryCover
                                    post={p}
                                    imgClassName="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                                />
                            </div>
                            <div className="p-4 flex flex-col gap-2 flex-1">
                                <div className="flex items-center gap-2 font-mono-brand text-[9.5px] uppercase tracking-[0.18em] text-[var(--ink-3)]">
                                    <span>{formatDate(p.published_at)}</span>
                                    <span className="w-1 h-1 rounded-full bg-[var(--line-3,#3a3a3d)]" />
                                    <span className="inline-flex items-center gap-1">
                                        <Clock size={10} /> {p.read_minutes} min
                                    </span>
                                </div>
                                <div className="font-display text-[17px] leading-tight text-[var(--ink)]">
                                    {p.title}
                                </div>
                                <span className="mt-auto pt-1 inline-flex items-center gap-1 font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--accent)]">
                                    Read
                                    <ArrowUpRight
                                        size={12}
                                        className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"
                                    />
                                </span>
                            </div>
                        </Link>
                    </motion.div>
                ))}
            </div>
        </section>
    );
};

const Story = () => {
    const { slug } = useParams();
    const [post, setPost] = useState(undefined); // undefined=loading, null=404

    const { scrollYProgress } = useScroll();
    const progress = useSpring(scrollYProgress, {
        stiffness: 140,
        damping: 30,
        mass: 0.3,
    });

    useEffect(() => {
        let alive = true;
        setPost(undefined);
        window.scrollTo(0, 0);
        fetch(`${API}/posts/${slug}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => alive && setPost(d))
            .catch(() => alive && setPost(null));
        return () => {
            alive = false;
        };
    }, [slug]);

    if (post === undefined) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center text-[var(--ink-3)]">
                <Loader2 size={24} className="animate-spin" />
            </div>
        );
    }

    if (post === null) {
        return (
            <section className="px-4 pt-32 pb-24 text-center">
                <div className="font-display-italic text-4xl text-[var(--ink)]">
                    Story not found.
                </div>
                <Link
                    to="/stories"
                    className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--line-2)] text-[13px] text-[var(--ink)] hover:border-[var(--accent)] transition-colors"
                >
                    <ArrowLeft size={14} /> All stories
                </Link>
            </section>
        );
    }

    const mdComponents = {
        img: ({ src, alt }) => (
            <img
                src={abs(src)}
                alt={alt || ''}
                loading="lazy"
                className="rounded-2xl border border-[var(--line)] my-8 w-full"
            />
        ),
        a: ({ href, children }) => (
            <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className="text-[var(--accent)] underline underline-offset-4 decoration-[var(--accent)]/40 hover:decoration-[var(--accent)]"
            >
                {children}
            </a>
        ),
    };

    const pageUrl = `${SITE_URL}/stories/${post.slug}`;

    return (
        <>
            <Seo
                title={`${post.title} · Stories · Himanshu Tanty`}
                description={post.subtitle || `A story by Himanshu Tanty.`}
                canonical={pageUrl}
                image={post.cover_url ? abs(post.cover_url) : undefined}
            />

            {/* Reading progress bar */}
            <motion.div
                aria-hidden
                data-testid="story-progress"
                style={{ scaleX: progress }}
                className="fixed top-0 left-0 right-0 z-[70] h-[3px] origin-left bg-[var(--accent)]"
            />

            <article
                data-testid="story-page"
                className="relative px-4 md:px-6 pt-24 md:pt-28 pb-24"
            >
                {/* Ambient glow behind the hero */}
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 top-0 h-[520px] opacity-[0.6]"
                    style={{
                        background:
                            'radial-gradient(60% 80% at 50% 0%, color-mix(in oklab, var(--accent) 12%, transparent), transparent 70%)',
                    }}
                />

                <div className="relative mx-auto max-w-[900px]">
                    {/* Top bar: back + share */}
                    <div className="flex items-center justify-between gap-4">
                        <Link
                            to="/stories"
                            data-testid="story-back"
                            data-cursor="hover"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--line-2)] bg-[var(--surface)] text-[13px] text-[var(--ink)] hover:border-[var(--accent)] transition-colors"
                        >
                            <ArrowLeft size={14} /> All stories
                        </Link>
                        <ShareRow title={post.title} url={pageUrl} />
                    </div>

                    {/* Header */}
                    <header className="mt-12 md:mt-16">
                        <div className="flex items-center gap-3 font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                            <span>{formatDate(post.published_at)}</span>
                            <span className="w-1 h-1 rounded-full bg-[var(--line-3,#3a3a3d)]" />
                            <span className="inline-flex items-center gap-1">
                                <Clock size={11} /> {post.read_minutes} min read
                            </span>
                        </div>

                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
                            data-testid="story-title"
                            className="mt-5 font-display leading-[1.03] text-[clamp(2rem,5.4vw,3.75rem)] text-[var(--ink)]"
                        >
                            {post.title}
                        </motion.h1>

                        {post.subtitle && (
                            <motion.p
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7, delay: 0.08 }}
                                className="mt-5 font-sans-brand text-[16px] md:text-[19px] leading-[1.55] text-[var(--ink-2)] max-w-[62ch]"
                            >
                                {post.subtitle}
                            </motion.p>
                        )}

                        {post.tags?.length > 0 && (
                            <div className="mt-6 flex flex-wrap gap-2">
                                {post.tags.map((t) => (
                                    <span key={t} className="chip">
                                        {t}
                                    </span>
                                ))}
                            </div>
                        )}
                    </header>

                    {/* Wide cover */}
                    <motion.figure
                        initial={{ opacity: 0, y: 28 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.12 }}
                        className="mt-10 md:mt-14"
                    >
                        {post.cover_url ? (
                            <img
                                src={abs(post.cover_url)}
                                alt={post.title}
                                className="w-full rounded-[22px] border border-[var(--line)] shadow-[0_30px_80px_-40px_rgba(0,0,0,0.6)]"
                            />
                        ) : (
                            <div className="aspect-[16/9] w-full rounded-[22px] overflow-hidden border border-[var(--line)] shadow-[0_30px_80px_-40px_rgba(0,0,0,0.6)]">
                                <BrandedCover
                                    title={post.title}
                                    tag={(post.tags || [])[0]}
                                    showTitle={false}
                                />
                            </div>
                        )}
                    </motion.figure>

                    {/* Body */}
                    <div className="story-article mt-12 md:mt-16">
                        <div
                            data-testid="story-body"
                            className="story-prose font-sans-brand text-[17px] leading-[1.75] text-[var(--ink-2)]"
                        >
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={mdComponents}
                            >
                                {post.body_md || ''}
                            </ReactMarkdown>
                        </div>

                        {/* Attachments */}
                        {post.attachments?.length > 0 && (
                            <div className="mt-12 pt-8 border-t border-[var(--line)]">
                                <div className="inline-flex items-center gap-2 font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)] mb-4">
                                    <Paperclip size={12} /> Attachments
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {post.attachments.map((a, i) => (
                                        <a
                                            key={i}
                                            href={abs(a.url)}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-cursor="hover"
                                            className="group flex items-center justify-between gap-3 rounded-xl border border-[var(--line)] bg-[var(--surface)] p-4 hover:border-[var(--accent)] transition-colors"
                                        >
                                            <div className="min-w-0">
                                                <div className="font-sans-brand text-[13px] text-[var(--ink)] truncate">
                                                    {a.filename}
                                                </div>
                                                {a.size ? (
                                                    <div className="font-mono-brand text-[10px] uppercase tracking-[0.18em] text-[var(--ink-3)] mt-0.5">
                                                        {humanSize(a.size)}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <Download
                                                size={16}
                                                className="text-[var(--ink-3)] group-hover:text-[var(--accent)] shrink-0"
                                            />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Footer: author + share + back */}
                        <div className="mt-14 pt-8 border-t border-[var(--line)]">
                            <div className="rounded-[22px] border border-[var(--line)] bg-[var(--surface)] p-6 md:p-7 flex items-center gap-5 flex-wrap">
                                <img
                                    src="/avatars/hero.webp"
                                    alt="Himanshu Tanty"
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                    }}
                                    className="w-14 h-14 rounded-full object-cover border border-[var(--line-2)]"
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                                        Written by
                                    </div>
                                    <div className="mt-0.5 font-display text-xl text-[var(--ink)]">
                                        Himanshu Tanty
                                    </div>
                                    <div className="font-sans-brand text-[13px] text-[var(--ink-2)]">
                                        Product Design Leader · Enterprise UX, AI and SaaS
                                    </div>
                                </div>
                                <ShareRow title={post.title} url={pageUrl} />
                            </div>

                            <Link
                                to="/stories"
                                data-cursor="hover"
                                className="mt-6 inline-flex items-center gap-2 font-mono-brand text-[11px] uppercase tracking-[0.2em] text-[var(--accent)] hover:opacity-80 transition-opacity"
                            >
                                Read more stories
                                <ArrowUpRight size={14} />
                            </Link>
                        </div>
                    </div>

                    {/* Related stories */}
                    <RelatedStories
                        currentSlug={post.slug}
                        currentTags={post.tags || []}
                    />
                </div>
            </article>
        </>
    );
};

export default Story;
